// =============================================================================
//  DOMAINE FACTURE — écran GÉNÉRIQUE du CORE. ⛔ Rien d'OMV ici.
// =============================================================================
//  🔴 CE QU'IL RÉPARE. Le module `metier_hub/factures/` portait ses modèles,
//  son service, son PDF et sa garde d'immuabilité — et AUCUNE route ne les
//  exposait. Deux factures existaient en base, produites par script.
//  ⇒ Backend + PDF ⛔ n'est PAS une fonctionnalité livrée : c'est une preuve
//  technique. Cet écran est ce qui la rend exerçable.
//
//  🔑 GÉNÉRIQUE À DESSEIN. Aucun préfixe de numéro, aucun libellé, aucun champ
//  propre à une verticale. OH Devis le montera la semaine prochaine sans y
//  toucher. ⛔ Un « InvoiceDomain » qui connaîtrait un tenant n'en serait pas un.
//
//  ⚠️ IL SE MONTE DE DEUX FAÇONS, sans rien réorganiser :
//    · en LISTE      — toutes les factures du tenant
//    · en ENCART     — `devisId` fourni ⇒ les factures NÉES de ce devis,
//                      ce qui rend la transformation devis → facture VISIBLE
//                      dans le parcours existant.
// =============================================================================
import { useCallback, useEffect, useMemo, useState } from "react";
import { usePermissions } from "../../contexts/PermissionContext";
import FactureSimple from "./FactureSimple";
import { BlocPaiement, BoutonEnvoi, TraceEnvoi } from "./FactureReglement";
import { FileText, Download, Loader2, Search, ArrowLeft, AlertTriangle,
         Lock, Unlock, Save, RotateCcw, FilePlus } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || "";
const BASE = `${API}/api/metier/factures`;

// ⚠️ Paires fond/texte dual-mode. ⛔ Jamais une couleur en dur : en mode clair,
// un texte clair sur fond clair de la même famille devient illisible.
const TONS = {
  brouillon: { bg: "var(--oh-stat-amber-bg)", text: "var(--oh-stat-amber-text)", libelle: "Brouillon" },
  emise:     { bg: "var(--oh-stat-blue-bg)",  text: "var(--oh-stat-blue-text)",  libelle: "Émise" },
  payee:     { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", libelle: "Payée" },
  paid:      { bg: "var(--oh-stat-green-bg)", text: "var(--oh-stat-green-text)", libelle: "Payée" },
  annulee:   { bg: "var(--oh-stat-red-bg)",   text: "var(--oh-stat-red-text)",   libelle: "Annulée" },
  avoir:     { bg: "var(--oh-stat-purple-bg)", text: "var(--oh-stat-purple-text)", libelle: "Avoir" },
};

const etiquette = { fontSize: 12, color: "var(--oh-text-secondary)", fontWeight: 500 };
const carte = {
  background: "var(--oh-bg-elevated)", border: "0.5px solid var(--oh-border)",
  borderRadius: 12, padding: 14,
};
const champ = {
  height: 36, borderRadius: 8, padding: "0 10px", fontSize: 13,
  background: "var(--oh-bg-surface)", color: "var(--oh-text)",
  border: "1px solid var(--oh-border)", width: "100%",
};
const btnDiscret = {
  display: "inline-flex", alignItems: "center", gap: 6, padding: "6px 12px",
  borderRadius: 8, fontSize: 12.5, cursor: "pointer", background: "transparent",
  border: "1px solid var(--oh-border)", color: "var(--oh-text-secondary)",
};

/** ⚠️ Un montant se lit d'un coup d'œil : espace milliers, virgule décimale,
 *  chiffres tabulaires pour que les colonnes s'alignent. */
function euros(v) {
  const n = Number(v);
  if (!Number.isFinite(n)) return "—";
  return n.toLocaleString("fr-FR", { minimumFractionDigits: 2,
                                     maximumFractionDigits: 2 }) + " €";
}

function dateFR(v) {
  if (!v) return "—";
  const d = new Date(v);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleDateString("fr-FR");
}

function Badge({ statut, nature, libelle }) {
  // 🔑 L'AVOIR PRIME sur le statut : c'est sa NATURE, et elle change le sens
  // du montant. ⛔ L'afficher « émis » comme une facture ferait lire un
  // encaissement là où il y a un remboursement.
  const cle = nature === "avoir" ? "avoir" : String(statut || "").toLowerCase();
  const t = TONS[cle] || { bg: "var(--oh-bg-surface)",
                           text: "var(--oh-text-secondary)",
                           libelle: statut || "—" };
  return (
    <span style={{ padding: "2px 8px", borderRadius: 6, fontSize: 11,
                   background: t.bg, color: t.text, whiteSpace: "nowrap" }}>
      {libelle || t.libelle}
    </span>
  );
}

/** 🔴 DEC-103 — LE SÉLECTEUR CATALOGUE d'une ligne de facture.
 *
 *  ⚠️ DÉFINI HORS DU COMPOSANT PARENT, et ce n'est pas un détail de style :
 *  un composant redéfini à chaque rendu du parent est REMONTÉ à chaque frappe,
 *  et le champ perd le focus. C'est le défaut déjà corrigé sur `MoneyInput`
 *  (`FinancementTab.jsx`, fichier protégé) — ⛔ on ne le réintroduit pas ici.
 *
 *  🔑 Il ⛔ ne DÉCIDE de rien : la liste des produits proposables vient du
 *  SERVEUR (`/factures/produits-selectionnables`), et le droit de corriger vient
 *  de `facture.modifiable`. L'écran ⛔ ne rejoue ni « actif », ni « prix
 *  provisoire », ni le point de gel.
 */
function SelecteurProduit({ ligne, facture, catalogue, occupe, surCorrection }) {
  const [choix, setChoix] = useState("");
  const [motif, setMotif] = useState("");

  // 🔴 APRÈS LE GEL : le sélecteur reste VISIBLE mais DÉSACTIVÉ, et le motif
  // est dit. ⛔ Le faire disparaître laisserait croire que la correction n'a
  // jamais existé ; un champ inerte sans explication ressemble à une panne.
  if (!facture.modifiable) {
    return (
      <div style={{ marginTop: 4 }} data-testid={`selecteur-gele-${ligne.id}`}>
        <select disabled style={{ ...champ, height: 30, opacity: 0.55,
                                  cursor: "not-allowed", maxWidth: 260 }}
                aria-label="Sélection produit (indisponible)">
          <option>{ligne.reference || "produit"}</option>
        </select>
        <p style={{ ...etiquette, marginTop: 4, lineHeight: 1.5 }}>
          {facture.motif_etat}
        </p>
      </div>
    );
  }

  return (
    <div style={{ marginTop: 6, display: "grid", gap: 4, maxWidth: 320 }}>
      <select value={choix} disabled={occupe} style={{ ...champ, height: 30 }}
              data-testid={`selecteur-produit-${ligne.id}`}
              aria-label="Remplacer le produit par une référence du catalogue"
              onChange={(e) => setChoix(e.target.value)}>
        <option value="">Remplacer par un produit du catalogue…</option>
        {(catalogue?.produits || []).map((p) => (
          <option key={p.id} value={p.id}>
            {p.reference}{p.fiche_cee ? ` · ${p.fiche_cee}` : ""}
            {p.prix_ht != null ? ` · barème ${p.prix_ht} €` : ""}
          </option>
        ))}
      </select>
      <input style={{ ...champ, height: 30 }} value={motif}
             data-testid={`motif-produit-${ligne.id}`} disabled={occupe}
             placeholder="Motif de la correction (obligatoire)"
             aria-label="Motif de la correction produit"
             onChange={(e) => setMotif(e.target.value)} />
      <button type="button" data-testid={`appliquer-produit-${ligne.id}`}
              disabled={occupe || !choix || !motif.trim()}
              onClick={async () => {
                const fait = await surCorrection(ligne.id, choix, motif.trim());
                if (fait) { setChoix(""); setMotif(""); }
              }}
              style={{ ...btnDiscret, height: 30, justifyContent: "center",
                       borderColor: "var(--oh-accent)",
                       color: "var(--oh-accent-text)" }}>
        Remplacer le produit
      </button>
      {/* ⚠️ LE PRIX EST ANNONCÉ COMME CONSERVÉ, AVANT le clic. Le barème du
          catalogue est affiché dans la liste : sans cette phrase, on croirait
          qu'il va s'appliquer. 🔑 C'est la garantie DEC-103, dite à l'écran. */}
      <p style={{ ...etiquette, lineHeight: 1.5 }}>
        ⛔ Le prix contractuel ({euros(ligne.prix_unitaire_ht)} HT) est
        CONSERVÉ — le barème catalogue ⛔ ne s'applique PAS.
      </p>
    </div>
  );
}


export default function FacturesDomaine({ devisId = null, titre = null,
                                          compact = false,
                                          // 🔑 La facture qu'on vient de créer : l'écran
                                          // s'ouvre DESSUS. ⛔ Rendre la liste et laisser
                                          // l'utilisateur la retrouver, c'est lui demander
                                          // de vérifier notre travail.
                                          ouvrirId = null,
                                          // ⚠️ Signale que `ouvrirId` a été CONSOMMÉ.
                                          // L'appelant peut alors oublier sa valeur —
                                          // ⛔ sans quoi elle rouvrirait la même facture
                                          // au prochain passage sur l'écran.
                                          onOuvert = null,
                                          // Ouvre la fiche client EXISTANTE (aucune écriture ici).
                                          onOuvrirClient = null }) {
  // 🔴 VERROU — capacité DÉCLARÉE, ⛔ pas un slug (voir DevisFacturesPage).
  const { isFeatureEnabled } = usePermissions();
  const facturationActive = isFeatureEnabled("commercial.factures");
  const [lot, setLot] = useState(null);
  const [choisie, setChoisie] = useState(null);
  const [filtre, setFiltre] = useState("");
  const [statut, setStatut] = useState("");
  const [erreur, setErreur] = useState(null);
  const [occupe, setOccupe] = useState(false);
  // ⚠️ La correction est un formulaire à DEUX champs dont le motif est
  // OBLIGATOIRE : le bouton reste inactif tant qu'il est vide, ⛔ plutôt que
  // d'envoyer une requête qu'on sait refusée.
  const [corr, setCorr] = useState({ total_ht: "", motif: "" });
  // 🔴 DEC-103 — le catalogue SÉLECTIONNABLE, lu au SERVEUR. ⛔ Jamais une
  // liste en dur : le catalogue réel arrivera derrière et alimentera le MÊME
  // composant, sans une ligne de plus ici.
  const [catalogue, setCatalogue] = useState(null);

  const charger = useCallback(async () => {
    setErreur(null);
    try {
      const p = new URLSearchParams();
      if (devisId) p.set("devis_id", devisId);
      if (statut) p.set("statut", statut);
      if (filtre.trim()) p.set("recherche", filtre.trim());
      const r = await fetch(`${BASE}?${p}`, { credentials: "include" });
      if (!r.ok) throw new Error(`Factures indisponibles (${r.status})`);
      setLot(await r.json());
    } catch (e) { setErreur(e.message); }
  }, [devisId, statut, filtre]);

  useEffect(() => { charger(); }, [charger]);

  const ouvrir = async (id) => {
    setOccupe(true); setErreur(null);
    try {
      const r = await fetch(`${BASE}/${id}`, { credentials: "include" });
      const corps = await r.json();
      if (!r.ok) { setErreur(corps.detail || `Refus (${r.status})`); return; }
      setChoisie(corps);
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  // ⚠️ `ouvrir` est redéfinie à chaque rendu — la mettre en dépendance d'un
  // effet le relancerait en boucle. On dépend de l'IDENTIFIANT, ⛔ pas de la
  // fonction, et on n'ouvre QUE si rien n'est déjà ouvert.
  useEffect(() => {
    if (ouvrirId && !choisie) {
      ouvrir(ouvrirId);
      if (onOuvert) onOuvert();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ouvrirId]);

  /** 🔴 ÉMETTRE — le brouillon devient opposable : numéro ET date, ensemble.
   *  ⛔ Le refus (mentions manquantes, déjà émise) s'AFFICHE, il ne plante pas. */
  const emettre = async (f) => {
    setOccupe(true); setErreur(null);
    try {
      const r = await fetch(`${BASE}/${f.id}/emettre`,
                            { method: "POST", credentials: "include" });
      const corps = await r.json();
      if (!r.ok) { setErreur(corps.detail || `Émission refusée (${r.status})`); return; }
      setChoisie(corps);
      charger();
    } catch (e) { setErreur(e.message); } finally { setOccupe(false); }
  };

  /** Appel générique qui remplace la facture affichée par la réponse serveur.
   *  🔑 On ⛔ ne recalcule RIEN côté écran : le serveur rend l'état, le cycle,
   *  les motifs et les relations. Un cycle réimplémenté ici divergerait. */
  const agir = async (chemin, corps, methode = "POST") => {
    setOccupe(true); setErreur(null);
    try {
      const r = await fetch(`${BASE}${chemin}`, {
        method: methode, credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(corps),
      });
      const rep = await r.json();
      if (!r.ok) { setErreur(rep.detail || `Refus (${r.status})`); return false; }
      // ⚠️ Après un avoir, la réponse est L'AVOIR : on recharge la facture
      // d'origine pour que la relation soit visible depuis elle.
      const cible = rep.facture_origine_id || rep.id;
      const r2 = await fetch(`${BASE}/${cible}`, { credentials: "include" });
      if (r2.ok) setChoisie(await r2.json());
      charger();
      return true;
    } catch (e) { setErreur(e.message); return false; }
    finally { setOccupe(false); }
  };

  /** 🔴 DEC-103 — remplacer le produit d'une ligne PAR SÉLECTION CATALOGUE.
   *  ⛔ AUCUN champ de prix n'est envoyé : le contrat de la route l'interdit
   *  (`extra: forbid`, aucun champ prix), et l'écran ⛔ n'en fabrique pas un. */
  const corrigerProduit = (ligneId, produitId, motifTexte) =>
    agir(`/${choisie.id}/lignes/${ligneId}/produit`,
         { produit_id: produitId, motif: motifTexte });

  // ⚠️ Le catalogue est chargé à l'ouverture d'une facture, ⛔ pas par ligne :
  // une facture à 10 lignes ⛔ ne doit pas déclencher 10 appels identiques.
  useEffect(() => {
    if (!choisie?.id) { setCatalogue(null); return; }
    let vivant = true;
    (async () => {
      try {
        const r = await fetch(`${BASE}/produits-selectionnables`,
                              { credentials: "include" });
        if (r.ok && vivant) setCatalogue(await r.json());
      } catch { /* ⚠️ un catalogue indisponible ⛔ ne casse pas l'écran :
                     le sélecteur reste vide, le reste de la facture s'affiche. */ }
    })();
    return () => { vivant = false; };
  }, [choisie?.id]);

  const voirPdf = (f) => {
    // 🔴 ON NE PROPOSE LE PDF QUE S'IL EST DISPONIBLE — le serveur le DIT
    // (`pdf_disponible`). ⛔ Offrir un bouton qui rendra 409 ferait passer une
    // garde pour une panne : un brouillon imprimé se confond avec une facture.
    if (f.pdf_disponible === false) {
      setErreur(f.motif_pdf_indisponible || "PDF indisponible");
      return;
    }
    window.open(`${BASE}/${f.id}/pdf`, "_blank", "noopener");
  };

  // T-211 · 27 — « Télécharger » : MÊME PDF, servi en `attachment` (enregistrement direct) ; « Voir le PDF » reste l'affichage.
  const telechargerPdf = (f) => {
    if (f.pdf_disponible === false) {
      setErreur(f.motif_pdf_indisponible || "PDF indisponible");
      return;
    }
    window.open(`${BASE}/${f.id}/pdf?telecharger=true`, "_blank", "noopener");
  };

  const factures = lot?.factures || [];
  const resume = useMemo(() => {
    if (!lot) return null;
    // ⚠️ Les deux natures sont comptées SÉPARÉMENT, par le serveur. ⛔ « 4
    // documents » ne dit pas « 4 factures » quand 2 sont des avoirs.
    return `${lot.nb_factures ?? lot.total} facture(s)`
         + (lot.nb_avoirs ? ` · ${lot.nb_avoirs} avoir(s)` : "")
         + (lot.tronquee ? ` · ${lot.rendues} affichées sur ${lot.total}` : "");
  }, [lot]);

  // ═══ LE DÉTAIL ═══════════════════════════════════════════════════════
  if (choisie) {
    // L'écran simplifié (rénovation) porte lui-même paiement et envoi : le détail
    // standard, ouvert dessous en « Options avancées », ne les redouble pas.
    const ecranSimple = choisie.profil_facturation === "renovation" && choisie.nature !== "avoir";
    const detailStandard = (
      <div style={{ display: "grid", gap: 12, gridTemplateColumns: "minmax(0, 1fr)" }} data-testid="facture-detail">
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <button type="button" style={btnDiscret} data-testid="facture-retour"
                  onClick={() => { setChoisie(null); setErreur(null); }}>
            <ArrowLeft style={{ width: 14, height: 14 }} /> Retour
          </button>
          <span style={{ fontSize: 16, fontWeight: 500, color: "var(--oh-text)" }}>
            {choisie.numero || "Sans numéro"}
          </span>
          <Badge statut={choisie.statut} nature={choisie.nature} libelle={choisie.statut_libelle} />
        </div>

        {erreur && (
          <div style={{ ...carte, background: "var(--oh-stat-red-bg)",
                        color: "var(--oh-stat-red-text)", fontSize: 13 }}
               data-testid="facture-erreur">{erreur}</div>
        )}

        {/* 🔴 LE MOTIF EST DIT AVANT LE CLIC. ⛔ Un brouillon ne « plante »
            pas : il explique pourquoi il ne s'imprime pas. */}
        {choisie.pdf_disponible === false && (
          <div style={{ ...carte, background: "var(--oh-stat-amber-bg)",
                        color: "var(--oh-stat-amber-text)", fontSize: 13,
                        lineHeight: 1.6 }}
               data-testid="facture-pdf-indisponible">
            <AlertTriangle style={{ width: 15, height: 15, verticalAlign: "-2px" }} />
            {" "}{choisie.motif_pdf_indisponible}
          </div>
        )}

        <div style={carte}>
          <div style={{ display: "grid", gap: 8,
                        gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))" }}>
            <div><p style={etiquette}>Client</p>
              <p style={{ margin: 0, color: "var(--oh-text)" }}>{choisie.client_nom || "—"}</p></div>
            <div><p style={etiquette}>Émise le</p>
              <p style={{ margin: 0, color: "var(--oh-text)" }}>{dateFR(choisie.date_emission)}</p></div>
            <div><p style={etiquette}>Échéance</p>
              <p style={{ margin: 0, color: "var(--oh-text)" }}>{dateFR(choisie.date_echeance)}</p></div>
            <div><p style={etiquette}>Total HT</p>
              <p style={{ margin: 0, color: "var(--oh-text)", fontVariantNumeric: "tabular-nums" }}>{euros(choisie.total_ht)}</p></div>
            <div><p style={etiquette}>TVA</p>
              <p style={{ margin: 0, color: "var(--oh-text)", fontVariantNumeric: "tabular-nums" }}>{euros(choisie.total_tva)}</p></div>
            <div><p style={etiquette}>Total TTC</p>
              <p style={{ margin: 0, fontWeight: 500, color: "var(--oh-text)", fontVariantNumeric: "tabular-nums" }}>{euros(choisie.total_ttc)}</p></div>
          </div>

          {/* ⚠️ Le lien vers le DEVIS D'ORIGINE : la transformation
              devis → facture se lit dans les DEUX sens. */}
          {(choisie.devis_numero || choisie.devis_id) && (
            <p style={{ ...etiquette, marginTop: 10 }} data-testid="facture-origine-devis">
              Issue du devis {choisie.devis_numero || choisie.devis_id}
            </p>
          )}
          {choisie.facture_origine_id && (
            <p style={{ ...etiquette, marginTop: 4 }}>
              Avoir sur la facture {choisie.facture_origine_id}
            </p>
          )}
        </div>

        {(choisie.lignes || []).length > 0 && (
          <div style={carte}>
            <p style={{ ...etiquette, marginBottom: 8 }}>Lignes</p>
            {/* 🔴 LE CATALOGUE EST-IL COMPLET ? L'écran le DIT.
                ⚠️ Une liste de 4 références en face de 17 produits ⛔ n'est PAS
                un catalogue de 4 produits : elle est FILTRÉE (désactivés, prix
                provisoires). Le taire ferait chercher un produit absent, puis
                choisir « un approchant » — exactement le défaut d'origine.
                🔑 Le texte vient du SERVEUR, ⛔ l'écran ne le compose pas. */}
            {choisie.modifiable && catalogue?.avertissement && (
              <p style={{ ...etiquette, marginBottom: 8, lineHeight: 1.6,
                          color: "var(--oh-stat-amber-text)" }}
                 data-testid="catalogue-avertissement">
                {catalogue.avertissement}
              </p>
            )}
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                <thead><tr style={{ color: "var(--oh-text-secondary)", textAlign: "left" }}>
                  <th style={{ padding: "4px 8px" }}>Désignation</th>
                  {/* 🔴 DEC-103 — la RÉFÉRENCE devient visible et corrigeable.
                      ⚠️ Elle ne s'affichait nulle part : l'utilisateur ne
                      pouvait donc ⛔ ni voir qu'elle était fausse, ⛔ ni la
                      corriger. C'est le besoin réel OMV — le devis part avec un
                      produit faux, on corrige à la facture. */}
                  <th style={{ padding: "4px 8px" }}>Produit / référence</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Qté</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>PU HT</th>
                  <th style={{ padding: "4px 8px", textAlign: "right" }}>Montant HT</th>
                </tr></thead>
                <tbody>
                  {choisie.lignes.map((l, i) => (
                    <tr key={l.id || i} style={{ borderTop: "0.5px solid var(--oh-border)",
                                                 color: "var(--oh-text)" }}>
                      <td style={{ padding: "6px 8px" }}>{l.designation}</td>
                      <td style={{ padding: "6px 8px" }}
                          data-testid={`ligne-produit-${l.id}`}>
                        <span style={{ fontVariantNumeric: "tabular-nums" }}>
                          {l.reference || "—"}
                        </span>
                        {/* ⚠️ LA CORRECTION EST SIGNALÉE : produit retenu ≠
                            produit hérité du devis. ⛔ Sans ce repère, une
                            correction serait invisible sur le document. */}
                        {l.produit_initial_id && l.produit_id
                         && l.produit_initial_id !== l.produit_id && (
                          <span style={{ ...etiquette, display: "block",
                                         color: "var(--oh-stat-amber-text)" }}
                                data-testid={`ligne-corrigee-${l.id}`}>
                            ⚠️ corrigé — produit du devis conservé en traçabilité
                          </span>
                        )}
                        {l.type_ligne === "produit" && (
                          <SelecteurProduit
                            ligne={l} facture={choisie} catalogue={catalogue}
                            occupe={occupe} surCorrection={corrigerProduit} />
                        )}
                      </td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{l.quantite}</td>
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{euros(l.prix_unitaire_ht)}</td>
                      {/* 🔴 `montant_ht`, ⛔ PAS `total_ht`. J'avais supposé le
                          nom du champ : la colonne affichait « — » sur TOUTES
                          les lignes. ⚠️ Même famille que `resume_geometrie` qui
                          rendait des compteurs — lire le CONTRAT, ⛔ pas le
                          deviner. */}
                      <td style={{ padding: "6px 8px", textAlign: "right", fontVariantNumeric: "tabular-nums" }}>{euros(l.montant_ht)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* 🔴 LE CYCLE, EN CLAIR. ⛔ Pas un bouton grisé : l'utilisateur doit
            savoir POURQUOI la facture est encore modifiable, ou pourquoi elle
            ne l'est plus — et quelle est l'ISSUE. Le texte vient du SERVEUR
            (`motif_etat`), ⛔ l'écran ne le compose pas. */}
        {choisie.motif_etat && (
          <div style={{ ...carte, fontSize: 13, lineHeight: 1.6,
                        background: choisie.modifiable
                          ? "var(--oh-stat-blue-bg)" : "var(--oh-stat-amber-bg)",
                        color: choisie.modifiable
                          ? "var(--oh-stat-blue-text)" : "var(--oh-stat-amber-text)" }}
               data-testid="facture-motif-etat">
            {choisie.modifiable
              ? <Unlock style={{ width: 15, height: 15, verticalAlign: "-3px" }} />
              : <Lock style={{ width: 15, height: 15, verticalAlign: "-3px" }} />}
            {" "}{choisie.motif_etat}
          </div>
        )}

        {/* 🔴 CORRIGER AVANT LE GEL — avec MOTIF OBLIGATOIRE. */}
        {choisie.modifiable && choisie.nature !== "avoir" && (
          <div style={carte} data-testid="facture-correction">
            <p style={{ ...etiquette, marginBottom: 8 }}>
              Corriger le total HT — toute correction est tracée (avant/après,
              auteur, motif)
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap",
                          alignItems: "flex-end" }}>
              <div style={{ flex: "0 0 140px" }}>
                <label style={etiquette} htmlFor="corr-ht">Total HT</label>
                <input id="corr-ht" style={champ} type="number" step="0.01"
                       data-testid="correction-total-ht"
                       value={corr.total_ht} disabled={occupe}
                       onChange={(e) => setCorr(c => ({ ...c, total_ht: e.target.value }))} />
              </div>
              <div style={{ flex: "1 1 220px" }}>
                <label style={etiquette} htmlFor="corr-motif">Motif (obligatoire)</label>
                <input id="corr-motif" style={champ} data-testid="correction-motif"
                       placeholder="Pourquoi cette correction ?"
                       value={corr.motif} disabled={occupe}
                       onChange={(e) => setCorr(c => ({ ...c, motif: e.target.value }))} />
              </div>
              <button type="button" data-testid="correction-appliquer"
                      disabled={occupe || !corr.motif.trim() || corr.total_ht === ""}
                      onClick={async () => {
                        const fait = await agir(`/${choisie.id}`, {
                          changements: { total_ht: Number(corr.total_ht) },
                          motif: corr.motif.trim() }, "PATCH");
                        if (fait) setCorr({ total_ht: "", motif: "" });
                      }}
                      style={{ ...btnDiscret, height: 36,
                               borderColor: "var(--oh-accent)",
                               color: "var(--oh-accent-text)",
                               cursor: occupe ? "wait" : "pointer" }}>
                <Save style={{ width: 14, height: 14 }} /> Corriger
              </button>
            </div>
          </div>
        )}

        {/* 🔴 DEC-089 — LA VALIDATION FINALE, SORTIE DES ÉTAPES ORDINAIRES.
            ⚠️ Elle figurait comme une puce parmi d'autres dans « Étapes
            possibles ». Or elle ATTRIBUE LE NUMÉRO DÉFINITIF **et** FIGE, dans
            la même opération, irréversiblement. ⛔ Une action irréversible qui
            se présente comme les autres est un piège de forme.
            🔑 Le texte et la possibilité viennent du SERVEUR — l'écran ⛔ ne
            décide ni du mot (« émettre » / « valider »), ni du droit. */}
        {choisie.validation_finale?.possible && (
          <div style={{ ...carte, borderColor: "var(--oh-accent)" }}
               data-testid="facture-validation-finale">
            <p style={{ margin: 0, fontSize: 13, lineHeight: 1.6,
                        color: "var(--oh-stat-amber-text)" }}
               data-testid="validation-finale-motif">
              {choisie.validation_finale.motif}
            </p>
            <button type="button" data-testid="validation-finale-appliquer"
                    disabled={occupe}
                    onClick={() => {
                      // ⚠️ CONFIRMATION EXIGÉE : le numéro consommé ⛔ ne se rend
                      // pas, et le gel ⛔ ne se lève pas. ⛔ Pas de clic
                      // accidentel sur une opération irréversible.
                      if (!window.confirm(
                        `Valider définitivement cette facture ?\n\n`
                        + `• un numéro de la série lui sera ATTRIBUÉ\n`
                        + `• elle sera FIGÉE : plus aucune correction\n`
                        + `• seule issue ensuite : avoir puis nouvelle facture\n\n`
                        + `Cette opération est IRRÉVERSIBLE.`)) return;
                      agir(`/${choisie.id}/transition`,
                           { vers: choisie.validation_finale.vers,
                             motif: "Validation finale — numéro et gel" });
                    }}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             marginTop: 10, padding: "6px 12px", borderRadius: 8,
                             fontSize: 12.5, border: "none",
                             cursor: occupe ? "wait" : "pointer",
                             background: "var(--oh-accent-solid)",
                             color: "var(--oh-accent-contrast)" }}>
              <Lock style={{ width: 14, height: 14 }} />
              Valider et numéroter la facture
            </button>
          </div>
        )}
        {!choisie.validation_finale?.possible
         && choisie.validation_finale?.motif && (
          <p style={{ ...etiquette, lineHeight: 1.6 }}
             data-testid="validation-finale-motif">
            {choisie.validation_finale.motif}
          </p>
        )}

        {/* LES AUTRES ÉTAPES DÉCLARÉES — ⛔ on ne propose QUE celles du serveur,
            et ⛔ JAMAIS la validation finale ici : elle a son propre bloc. */}
        {(choisie.etapes_possibles || []).length > 0 && (
          <div style={carte} data-testid="facture-transitions">
            <p style={{ ...etiquette, marginBottom: 8 }}>
              Étapes possibles · facture {(choisie.statut_libelle || "").toLowerCase()}
            </p>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {choisie.etapes_possibles.map(({ vers, libelle }) => (
                <button key={vers} type="button" disabled={occupe}
                        data-testid={`transition-${vers}`}
                        onClick={() => agir(`/${choisie.id}/transition`,
                                            { vers, motif: libelle })}
                        style={{ ...btnDiscret, borderColor: "var(--oh-border)",
                                 color: "var(--oh-text-secondary)" }}>
                  {libelle}
                </button>
              ))}
            </div>
          </div>
        )}

        {!ecranSimple && <BlocPaiement facture={choisie} occupe={occupe} onAgir={agir} />}

        {/* 🔴 LA RELATION facture ↔ avoir ↔ nouvelle facture.
            🔑 L'avoir ⛔ ne supprime JAMAIS l'original : les deux coexistent, et
            l'écran doit le montrer — sinon l'utilisateur croit avoir annulé. */}
        {(choisie.avoirs?.length > 0 || choisie.origine
          || choisie.fratrie_devis?.length > 0) && (
          <div style={carte} data-testid="facture-relations">
            <p style={{ ...etiquette, marginBottom: 8 }}>Documents liés</p>
            {choisie.origine && (
              <p style={{ margin: "4px 0", fontSize: 13, color: "var(--oh-text)" }}
                 data-testid="relation-origine">
                ↩ Cet avoir corrige la facture{" "}
                <strong>{choisie.origine.numero || choisie.origine.id}</strong>
                {" "}— ⛔ elle existe toujours.
              </p>
            )}
            {(choisie.avoirs || []).map((a) => (
              <p key={a.id} style={{ margin: "4px 0", fontSize: 13,
                                     color: "var(--oh-text)" }}
                 data-testid={`relation-avoir-${a.id}`}>
                ⊖ Avoir <strong>{a.numero}</strong> · {euros(a.total_ttc)}
                {" "}— ⛔ la facture d'origine n'est PAS supprimée.
              </p>
            ))}
            {(choisie.fratrie_devis || []).map((f) => (
              <p key={f.id} style={{ margin: "4px 0", fontSize: 13,
                                     color: "var(--oh-text)" }}
                 data-testid={`relation-fratrie-${f.id}`}>
                ↪ Autre facture du même devis :{" "}
                {/* ⚠️ « sans numéro » ⛔ n'est PAS « brouillon ». Mesuré à
                    l'écran le 03/09 : « brouillon (validee) » — une
                    contradiction affichée. Dans le cycle énergie le gel tombe
                    à `validee` alors que le numéro n'est posé qu'à l'émission :
                    une facture FIGÉE peut n'avoir aucun numéro. ⛔ Deviner le
                    statut depuis l'absence de numéro est faux. */}
                <strong>{f.numero || "sans numéro"}</strong> ({f.statut_libelle || f.statut})
              </p>
            ))}
          </div>
        )}

        {/* 🔴 LE SOLDE, ET LA DERNIÈRE MARCHE DU PARCOURS.
            ⚠️ Sans ce bloc, l'écran montrait « facture 2 565 € » et
            « avoir −2 565 € » côte à côte, et laissait l'utilisateur faire la
            soustraction — puis se demander pourquoi son devis restait
            infacturable. 🔑 Les deux chiffres et l'issue viennent du SERVEUR. */}
        {choisie.solde && choisie.solde.montant_credite > 0 && (
          <div style={carte} data-testid="facture-solde">
            <p style={{ ...etiquette, marginBottom: 8 }}>Solde après avoirs</p>
            <p style={{ margin: 0, fontSize: 13, color: "var(--oh-text)" }}>
              Facturé {euros(choisie.solde.total_ttc)} · crédité{" "}
              {euros(choisie.solde.montant_credite)} ⇒{" "}
              <strong data-testid="facture-solde-restant">
                reste dû {euros(choisie.solde.solde_ttc)}
              </strong>
            </p>
            {choisie.motif_refacturation && (
              <p style={{ ...etiquette, marginTop: 8, lineHeight: 1.6,
                          color: choisie.refacturation_possible
                            ? "var(--oh-stat-green-text)"
                            : "var(--oh-text-secondary)" }}
                 data-testid="facture-motif-refacturation">
                {choisie.motif_refacturation}
              </p>
            )}
            {/* 🔑 REFACTURER — le geste qui termine le parcours. ⛔ Proposé
                seulement quand le serveur déclare le devis de nouveau
                facturable : l'écran ⛔ ne redérive pas l'éligibilité. */}
            {/* 🔴 VERROU — MÊME capacité déclarée que l'action « Facturer » de
                la grille : une seule règle, deux écrans. */}
            {facturationActive && choisie.refacturation_possible && choisie.devis_id && (
              <button type="button" data-testid="facture-refacturer"
                      disabled={occupe} style={{ ...btnDiscret, marginTop: 10,
                        borderColor: "var(--oh-accent)",
                        color: "var(--oh-accent-text)" }}
                      onClick={() => agir("/depuis-devis",
                                          { devis_id: choisie.devis_id })}>
                <FilePlus style={{ width: 14, height: 14 }} /> Refacturer le devis
                {choisie.devis_numero ? ` ${choisie.devis_numero}` : ""}
              </button>
            )}
          </div>
        )}

        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          {/* 🔴 CRÉER UN AVOIR — proposé EXACTEMENT quand le serveur le dit.
              ⚠️ Une seule règle, la sienne : `avoir_possible` suit le point de
              gel. ⛔ L'écran ne redérive pas une seconde condition. */}
          {choisie.avoir_possible && choisie.profil_facturation !== "renovation" && (
            <button type="button" data-testid="facture-avoir" disabled={occupe}
                    onClick={() => {
                      const motif = window.prompt(
                        "Motif de l'avoir (obligatoire) — l'original ne sera pas supprimé :");
                      if (motif && motif.trim()) {
                        agir(`/${choisie.id}/avoir`, { motif: motif.trim() });
                      }
                    }}
                    style={{ ...btnDiscret, borderColor: "var(--oh-stat-purple-text)",
                             color: "var(--oh-stat-purple-text)" }}>
              <RotateCcw style={{ width: 14, height: 14 }} /> Créer un avoir
            </button>
          )}
          {!choisie.avoir_possible && choisie.motif_avoir
           && choisie.nature !== "avoir" && (
            <p style={{ ...etiquette, alignSelf: "center" }}
               data-testid="facture-motif-avoir">{choisie.motif_avoir}</p>
          )}
          {/* 🔴 ÉMETTRE : l'action PRIMAIRE — mais SEULEMENT si le cycle du
              tenant déclare cette transition. ⚠️ Le critère était
              `statut === "brouillon"`, et il PIÉGEAIT : dans le cycle énergie,
              « emise » n'est pas un état du cycle, donc une facture émise
              devenait sans transition, non figée et sans avoir possible. ⛔ Un
              bouton qui mène à un état sans issue est pire qu'un bouton absent.
              🔑 Le serveur décide (`emission_possible`), l'écran obéit. */}
          {choisie.emission_possible && (
            <button type="button" data-testid="facture-emettre" disabled={occupe}
                    onClick={() => emettre(choisie)}
                    style={{ display: "inline-flex", alignItems: "center", gap: 6,
                             padding: "6px 12px", borderRadius: 8, fontSize: 12.5,
                             cursor: occupe ? "wait" : "pointer", border: "none",
                             background: "var(--oh-accent-solid)",
                             color: "var(--oh-accent-contrast)" }}>
              {occupe ? <Loader2 style={{ width: 14, height: 14 }} />
                      : <FileText style={{ width: 14, height: 14 }} />}
              Émettre la facture
            </button>
          )}
          {/* ⛔ Pas de bouton grisé, ⛔ pas de silence : le MOTIF. */}
          {!choisie.emission_possible && choisie.motif_emission && (
            <p style={{ ...etiquette, alignSelf: "center", maxWidth: 520,
                        lineHeight: 1.6 }}
               data-testid="facture-motif-emission">{choisie.motif_emission}</p>
          )}
          {choisie.pdf_disponible !== false && (
            <button type="button" data-testid="facture-telecharger"
                    onClick={() => telechargerPdf(choisie)}
                    style={{ ...btnDiscret, borderColor: "var(--oh-accent)",
                             color: "var(--oh-accent-text)", width: "fit-content" }}>
              <Download style={{ width: 14, height: 14 }} /> Télécharger
            </button>
          )}
          {!ecranSimple && <BoutonEnvoi facture={choisie} occupe={occupe} onAgir={agir} />}
          {choisie.pdf_disponible !== false && (
            <button type="button" data-testid="facture-pdf"
                    onClick={() => voirPdf(choisie)}
                    style={{ ...btnDiscret, width: "fit-content" }}>
              <Download style={{ width: 14, height: 14 }} /> Voir le PDF
            </button>
          )}
        </div>
        {!ecranSimple && <TraceEnvoi facture={choisie} />}
      </div>
    );
    // Cycle « renovation » (Mario) : lecture simple + Modifier / Émettre ; le détail standard reste accessible (options avancées).
    // Gate = PROFIL de facturation déclaré par le serveur, jamais le nom du cycle
    // (Mario partage le cycle « classique » avec amo/régie).
    return ecranSimple
      ? <FactureSimple facture={choisie} onOuvrirClient={onOuvrirClient} catalogue={catalogue} erreur={erreur} occupe={occupe}
          onRetour={() => { setChoisie(null); setErreur(null); }} onEmettre={emettre} onAgir={agir}
          onCorrigerProduit={corrigerProduit} onVoirPdf={voirPdf} onTelecharger={telechargerPdf} detailStandard={detailStandard}
          SelecteurProduit={SelecteurProduit} />
      : detailStandard;
  }

  // ═══ LA LISTE ════════════════════════════════════════════════════════
  return (
    <div style={{ display: "grid", gap: 12 }} data-testid="factures-domaine">
      <div style={{ display: "flex", alignItems: "baseline", gap: 10,
                    flexWrap: "wrap" }}>
        <span style={{ fontSize: 15, fontWeight: 500, color: "var(--oh-text)" }}>
          {titre || (devisId ? "Factures issues de ce devis" : "Factures")}
        </span>
        {resume && (
          <span style={etiquette} data-testid="factures-resume">{resume}</span>
        )}
      </div>

      {!compact && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <div style={{ position: "relative", flex: "1 1 220px", maxWidth: 340 }}>
            <Search style={{ width: 14, height: 14, position: "absolute",
                             left: 10, top: 11, color: "var(--oh-text-muted)" }} />
            <input value={filtre} onChange={(e) => setFiltre(e.target.value)}
                   data-testid="factures-filtre"
                   placeholder="Numéro ou client…"
                   style={{ width: "100%", height: 36, borderRadius: 8,
                            padding: "0 10px 0 30px", fontSize: 13,
                            background: "var(--oh-bg-elevated)",
                            color: "var(--oh-text)",
                            border: "0.5px solid var(--oh-border)" }} />
          </div>
          <select value={statut} onChange={(e) => setStatut(e.target.value)}
                  data-testid="factures-statut"
                  style={{ height: 36, borderRadius: 8, padding: "0 10px",
                           fontSize: 13, background: "var(--oh-bg-elevated)",
                           color: "var(--oh-text)",
                           border: "0.5px solid var(--oh-border)" }}>
            <option value="">Tous les statuts</option>
            {["brouillon", "emise", "payee", "annulee", "avoir"].map((s) => (
              <option key={s} value={s}>{TONS[s]?.libelle || s}</option>
            ))}
          </select>
        </div>
      )}

      {erreur && (
        <div style={{ ...carte, background: "var(--oh-stat-red-bg)",
                      color: "var(--oh-stat-red-text)", fontSize: 13 }}
             data-testid="factures-erreur">{erreur}</div>
      )}

      {!lot && !erreur && (
        <p style={{ ...etiquette, display: "flex", gap: 6, alignItems: "center" }}>
          <Loader2 style={{ width: 14, height: 14 }} className="animate-spin" />
          Lecture des factures…
        </p>
      )}

      {lot && factures.length === 0 && (
        <div style={{ ...carte, fontSize: 13, lineHeight: 1.6,
                      color: "var(--oh-text-secondary)" }}
             data-testid="factures-vide">
          {/* ⚠️ On dit ce que ça SIGNIFIE, ⛔ pas « aucun résultat ». */}
          {devisId
            ? "Ce devis n'a pas encore produit de facture."
            : (filtre || statut)
              ? "Aucune facture ne correspond à ce filtre."
              : "Aucune facture pour l'instant."}
        </div>
      )}

      <div style={{ display: "grid", gap: 8 }}>
        {factures.map((f) => (
          <button key={f.id} type="button" onClick={() => ouvrir(f.id)}
                  disabled={occupe} data-testid={`facture-ligne-${f.numero || f.id}`}
                  style={{ ...carte, textAlign: "left", cursor: "pointer",
                           display: "grid", gap: 6 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "center",
                          flexWrap: "wrap" }}>
              <FileText style={{ width: 15, height: 15, color: "var(--oh-text-muted)" }} />
              <span style={{ fontWeight: 500, color: "var(--oh-text)" }}>
                {/* ⚠️ Même correction que ci-dessus : la LIGNE de liste
                    affichait « Brouillon » pour toute facture sans numéro,
                    y compris une facture FIGÉE du cycle énergie. Le badge
                    voisin porte déjà le statut RÉEL — ⛔ il ne doit pas le
                    contredire. */}
                {f.numero || "Sans numéro"}
              </span>
              <Badge statut={f.statut} nature={f.nature} libelle={f.statut_libelle} />
              <span style={{ ...etiquette, marginLeft: "auto",
                             fontVariantNumeric: "tabular-nums" }}>
                {euros(f.total_ttc)} TTC
              </span>
            </div>
            <div style={{ fontSize: 12.5, color: "var(--oh-text-secondary)" }}>
              {f.client_nom || "—"} · {dateFR(f.date_emission)}
              {f.devis_numero ? ` · devis ${f.devis_numero}` : ""}
            </div>
          </button>
        ))}
      </div>
    </div>
  );
}
